import { parseLegacyReminderKey, parseReminderKey } from '@wiztime/core';

/**
 * Names shared by the page and the service-worker handler `public/reminder-sw.js` (which cannot
 * import this module and repeats them): notification tags, the message a notification click
 * posts to the page, and the query a click opens a new window with when no page is left.
 */
export const REMINDER_TAG_PREFIX = 'wiztime-reminder';
export const REMINDER_MESSAGE = 'wiztime-reminder';
export const REMINDER_QUERY = 'reminder';
export const REMINDER_KEYS_QUERY = 'reminderKeys';

export const ReminderAction = {
  /** The notification body was clicked: show the day. */
  Open: 'open',
  /** "Done": tick the item. */
  Done: 'done',
  /** "Remind me in 10 minutes". */
  Snooze: 'snooze',
} as const;
export type ReminderAction = (typeof ReminderAction)[keyof typeof ReminderAction];

export interface ReminderCommand {
  readonly action: ReminderAction;
  /** Reminder keys (`itemId|occursAt`, or the pre-upgrade `personalDate|itemId`) of the notification. */
  readonly keys: readonly string[];
}

const ACTIONS: readonly string[] = Object.values(ReminderAction);
const isAction = (value: unknown): value is ReminderAction => typeof value === 'string' && ACTIONS.includes(value);
// A notification shown before the upgrade carries a key of the older format; it still works.
const isKey = (value: unknown): value is string =>
  typeof value === 'string' && (parseReminderKey(value) !== null || parseLegacyReminderKey(value) !== null);

/** The command in a message from the service worker, or null for any other message. */
export function readReminderMessage(data: unknown): ReminderCommand | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, action, keys } = data as { type?: unknown; action?: unknown; keys?: unknown };
  if (type !== REMINDER_MESSAGE || !isAction(action)) return null;
  return { action, keys: Array.isArray(keys) ? keys.filter(isKey) : [] };
}

/** The command in the start-up URL query (`?reminder=done&reminderKeys=…`), or null. */
export function readReminderQuery(search: string): ReminderCommand | null {
  const params = new URLSearchParams(search);
  const action = params.get(REMINDER_QUERY);
  if (!isAction(action)) return null;
  const keys = (params.get(REMINDER_KEYS_QUERY) ?? '').split(',').filter(isKey);
  return { action, keys };
}
